import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, ExternalLink, Shield, Linkedin, Mail, Phone, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PortalLeadershipProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ bio: '', full_bio: '', email: '', phone: '', linkedin: '' });

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/portal/leadership-profile`, { withCredentials: true });
      setProfile(res.data);
      setForm({
        bio: res.data.bio || '',
        full_bio: res.data.full_bio || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        linkedin: res.data.linkedin || '',
      });
    } catch (err) {
      // Not linked or not leadership
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/portal/leadership-profile`, form, { withCredentials: true });
      setProfile(res.data);
      toast.success('Leadership profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const inputClass = "w-full border border-slate-200 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]";

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-[#002249] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-6 lg:p-8" data-testid="leadership-profile-not-linked">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#002249] mb-2">Leadership Profile Not Linked</h2>
          <p className="text-slate-500 text-sm">Your account is not linked to a leadership profile yet. Please contact the Super Admin to link your account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="portal-leadership-profile">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#002249]">My Leadership Profile</h1>
            <p className="text-sm text-slate-500 mt-1">Update your public-facing leadership information</p>
          </div>
          <a href={`/leadership/${profile.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-[#002249] hover:text-[#E63946] transition-colors" data-testid="view-public-profile">
            <ExternalLink className="w-4 h-4" />View Public Page
          </a>
        </div>

        {/* Read-only info */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-5 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[#002249]" />
            <span className="text-xs font-bold text-[#002249] tracking-wide uppercase">Admin-Managed Fields</span>
          </div>
          <p className="text-xs text-slate-400 mb-3">These fields are managed by the Super Admin. Contact admin to request changes.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Name</span>
              <p className="text-sm font-semibold text-[#002249]">{profile.name}</p>
            </div>
            <div>
              <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Title</span>
              <p className="text-sm font-semibold text-[#002249]">{profile.title}</p>
            </div>
            <div>
              <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Category</span>
              <p className="text-sm font-semibold text-[#002249] capitalize">{profile.category?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>

        <div className="max-w-3xl space-y-6">
          {/* Bio */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4 text-[#002249]" />
              <h2 className="font-bold text-[#002249]">Biography</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Short Bio (shown on leadership cards)</label>
                <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} className={`${inputClass} min-h-[80px]`} placeholder="A brief description shown on the leadership page cards..." data-testid="self-edit-bio" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Full Biography (shown on your profile page)</label>
                <textarea value={form.full_bio} onChange={e => setForm({ ...form, full_bio: e.target.value })} className={`${inputClass} min-h-[140px]`} placeholder="Detailed biography for your individual leadership profile page..." data-testid="self-edit-full-bio" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-4 h-4 text-[#002249]" />
              <h2 className="font-bold text-[#002249]">Contact Information</h2>
            </div>
            <p className="text-xs text-slate-400 mb-4">This contact info appears on your public leadership profile page.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1"><Mail className="w-3 h-3 inline mr-1" />Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} placeholder="Public contact email" data-testid="self-edit-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1"><Phone className="w-3 h-3 inline mr-1" />Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} placeholder="Public phone number" data-testid="self-edit-phone" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1"><Linkedin className="w-3 h-3 inline mr-1" />LinkedIn</label>
                <input value={form.linkedin} onChange={e => setForm({ ...form, linkedin: e.target.value })} className={inputClass} placeholder="https://linkedin.com/in/..." data-testid="self-edit-linkedin" />
              </div>
            </div>
          </div>

          {/* Save */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="bg-[#002249] hover:bg-[#001833] text-white rounded-sm px-6" data-testid="save-leadership-profile">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PortalLeadershipProfile;
